// Entity Controller – wraps entity service with validation
import EntityService from "@services/entity.service";
import Config from "@config";
import { getSession } from "@hooks/useSession";
import {CurrentEntityState} from "@state";
import State, { STATE } from "@state";

/**
 * Fetch entities for current user and store them in state.
 * @param {string} entityType – default "cro"
 * @returns {Promise<object>} success or error object
 */
const fetchUserEntities = async (entityType = "cro") => {
  try {
    const { user } = getSession() || {};
    if (!user?.userId) {
      return {
        success: false,
        message: "Missing userId in session",
        errorCode: "entity-no-session-user",
        errorKey: 667210,
      };
    }

    const data = await EntityService.getEntitiesByUserId({
      userId: user.userId,
      entityType,
    });

    if (data?.success === false) return data;

    // Store list in state
    
    if (Array.isArray(data) && data.length > 0) {
      State.setValue(CurrentEntityState.ENTITIES_LIST, data);
      !STATE[CurrentEntityState.CURRENT_ENTITY_ID] && State.setValue(CurrentEntityState.CURRENT_ENTITY_ID, data[0].id);
      const entityData = data.find(entity => entity.id === STATE[CurrentEntityState.CURRENT_ENTITY_ID]);
      State.setValue(CurrentEntityState.CURRENT_ENTITY_DATA, entityData);
    }

    return { success: true, data };
  } catch (error) {
    if (!Config.IN_PROD) console.error("[entity.controller]", error);
    return {
      success: false,
      message: "Unexpected error while fetching entities",
      fromError: error,
      errorCode: "entity-fetch-error",
      errorKey: 667211,
    };
  }
};

/**
 * Retrieves the entity ID from the backend given a did_address.
 * @param {string} didAddress - The user's did_address.
 * @returns {Promise<string|null>} The entity ID or null.
 */
const getEntityIdByDidAddress = async (didAddress) => {
  console.log("=== didAddress (frontend controller) ===", didAddress)
  try {
    const entityId = await EntityService.getEntityIdByDidAddress(didAddress)
    return entityId
  } catch (error) {
    console.error("Frontend Controller error in getEntityIdByDidAddress:", error)
    return null
  }
}

/**
 * Fetches entity information from the service.
 * @param {string} entityId - The ID of the entity.
 * @returns {Promise<object>} Success or error object.
 */
const getEntityById = async (entityId) => {
  try {
    if (!entityId) {
      return {
        success: false,
        message: "Missing entityId",
        errorCode: "entity-info-no-id",
        errorKey: 667212,
      }
    }
    const data = await EntityService.getEntityById(entityId) // Pass entityId to service
    return { success: true, data }
  } catch (error) {
    if (!Config.IN_PROD) console.error("[entity.controller]", error)
    return {
      success: false,
      message: "Unexpected error fetching entity information",
      fromError: error,
      errorCode: "entity-info-fetch-error",
      errorKey: 667213,
    }
  }
}

 /**
 * Registers an investigator by calling the backend service.
 * @param {object} params
 * @param {string} params.userId - The ID of the user (investigator).
 * @param {string} params.siteId - The ID of the site the investigator is associated with.
 * @param {string} [params.croEntityId] - The ID of the CRO entity the investigator belongs to (optional).
 * @returns {Promise<object>} Success or error object.
 */

 const registerInvestigator = async ({ userId, siteId, croEntityId }) => {
  try {
    if (!userId) {
      return {
        success: false,
        message: "Missing userId for investigator registration.",
        errorCode: "investigator-controller-missing-user-id",
        errorKey: 667214, 
      }
    }
    if (!siteId) {
      return {
        success: false,
        message: "Missing siteId for investigator registration.",
        errorCode: "investigator-controller-missing-site-id",
        errorKey: 667215,
      }
    }

    if (!croEntityId) {
      return {
        success: false,
        message: "Missing croEntityId for investigator registration.",
        errorCode: "investigator-controller-missing-site-id",
        errorKey: 667115,
      }
    }

    const response = await EntityService.registerInvestigator({ userId, siteId, croEntityId })

    if (response?.success === false) {
      return response
    }

    return { success: true, data: response }
  } catch (error) {
    if (!Config.IN_PROD) console.error("[entity.controller] registerInvestigator:", error)
    return {
      success: false,
      message: "Unexpected error during investigator registration.",
      fromError: error,
      errorCode: "investigator-registration-error",
      errorKey: 667216, 
  }
}
}

/**
 * Met à jour les informations de l'entité courante.
 * @param {object} contact – Nouvelles données de contact à enregistrer.
 * @returns {Promise<object>} Résultat du succès ou erreur.
 */
const updateEntityById = async (contact) => {
  try {
    const entityId = STATE[CurrentEntityState.CURRENT_ENTITY_ID];
    if (!entityId || !contact) {
      return {
        success: false,
        message: "Champs requis manquants: entityId ou contact",
        errorCode: "update-entity-missing-fields",
        errorKey: 667220,
      };
    }

    const result = await EntityService.updateEntityById(entityId, { contact });

    if (result?.success === false) return result;

    const currentEntities = STATE[CurrentEntityState.ENTITIES_LIST] || [];
    const updatedEntities = currentEntities.map((e) =>
      e.id === entityId ? { ...e, ...contact } : e
    );

    State.setValue(CurrentEntityState.ENTITIES_LIST, updatedEntities);
    State.setValue(CurrentEntityState.CURRENT_ENTITY_DATA, {
      ...STATE[CurrentEntityState.CURRENT_ENTITY_DATA],
      ...contact,
    });

    return { success: true, data: result };
  } catch (error) {
    if (!Config.IN_PROD) console.error("[updateEntity.controller]", error);
    return {
      success: false,
      message: "Erreur inattendue lors de la mise à jour de l'entité",
      fromError: error,
      errorCode: "update-entity-error",
      errorKey: 667221,
    };
  }
};


export default {
  fetchUserEntities,
  getEntityById,
  getEntityIdByDidAddress,
  registerInvestigator,
  updateEntityById,
}